<template>
  <div class="maintenance-container">
    <div class="maintenance-header">
      <h2>运维管理</h2>
      <p class="description">执行系统维护任务和管理操作</p>
    </div>

    <div class="maintenance-content">
      <!-- ADSENSE_KEY 配置来源 -->
      <el-card class="task-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span class="card-title">ADSENSE_KEY 配置来源</span>
          </div>
        </template>
        <div class="task-actions">
          <div class="adsense-key-row">
            <span class="adsense-key-label">来源：</span>
            <el-select
              v-model="adsenseKeySource"
              style="width: 180px;"
              :disabled="adsenseKeyLoading"
              @change="handleAdSenseKeySourceChange"
            >
              <el-option label="文件配置" value="file" />
              <el-option label="数据库" value="db" />
            </el-select>
          </div>

          <div v-if="adsenseKeySource === 'db'" class="adsense-key-panel">
            <div v-if="adsenseKeyStatus?.hasDbConfig">
              <div class="adsense-key-actions-inline">
                <el-button type="success" plain disabled>
                  已配置
                </el-button>
                <el-button
                  type="danger"
                  plain
                  :loading="adsenseKeyLoading"
                  @click="handleDeleteAdSenseKeyDb"
                >
                  删除并重新添加
                </el-button>
              </div>
            </div>
            <div v-else>
              <el-input
                v-model="adsenseKeyInput"
                type="textarea"
                :rows="8"
                placeholder='请输入 ADSENSE_KEY JSON，例如 {"oauth_client_id":"...","oauth_client_secret":"...","publisher_id":"...","refresh_token":"..."}'
                resize="vertical"
              />
              <div class="adsense-key-actions-inline">
                <el-button
                  type="primary"
                  :loading="adsenseKeyLoading"
                  @click="handleSaveAdSenseKeyDb"
                >
                  加密保存到数据库
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { MaintenanceApi } from '../../api/maintenanceApi';

export default defineComponent({
  name: 'Maintenance',
  data() {
    return {
      adsenseKeyLoading: false,
      adsenseKeyStatus: null as any,
      adsenseKeySource: 'file' as 'file' | 'db',
      adsenseKeyInput: '',
    };
  },
  mounted() {
    this.loadAdSenseKeyStatus();
  },
  methods: {
    async loadAdSenseKeyStatus(): Promise<void> {
      try {
        this.adsenseKeyLoading = true;
        const result = await MaintenanceApi.getAdSenseKeyStatus();
        if (!result.success) {
          throw new Error(result.error || '获取状态失败');
        }
        this.adsenseKeyStatus = result.data || null;
        this.adsenseKeySource = (result.data?.source || 'file') as 'file' | 'db';
      } catch (error: any) {
        ElMessage.error('加载 ADSENSE_KEY 状态失败: ' + (error.message || '未知错误'));
      } finally {
        this.adsenseKeyLoading = false;
      }
    },

    async handleAdSenseKeySourceChange(source: 'file' | 'db'): Promise<void> {
      try {
        this.adsenseKeyLoading = true;
        const result = await MaintenanceApi.setAdSenseKeySource(source);
        if (!result.success) {
          throw new Error(result.error || '切换来源失败');
        }
        this.adsenseKeyStatus = result.data || null;
        this.adsenseKeySource = (result.data?.source || source) as 'file' | 'db';
        ElMessage.success('来源切换成功');
      } catch (error: any) {
        ElMessage.error('切换来源失败: ' + (error.message || '未知错误'));
      } finally {
        this.adsenseKeyLoading = false;
      }
    },

    async handleSaveAdSenseKeyDb(): Promise<void> {
      if (!this.adsenseKeyInput || !this.adsenseKeyInput.trim()) {
        ElMessage.warning('请先输入 ADSENSE_KEY JSON');
        return;
      }
      let payload: Record<string, any>;
      try {
        payload = JSON.parse(this.adsenseKeyInput);
      } catch (error) {
        ElMessage.error('ADSENSE_KEY JSON 格式无效');
        return;
      }
      try {
        this.adsenseKeyLoading = true;
        const result = await MaintenanceApi.saveAdSenseKeyToDb(payload);
        if (!result.success) {
          throw new Error(result.error || '保存失败');
        }
        this.adsenseKeyStatus = result.data || null;
        this.adsenseKeySource = (result.data?.source || 'db') as 'file' | 'db';
        this.adsenseKeyInput = '';
        ElMessage.success('ADSENSE_KEY 已加密保存到数据库');
      } catch (error: any) {
        ElMessage.error('保存失败: ' + (error.message || '未知错误'));
      } finally {
        this.adsenseKeyLoading = false;
      }
    },

    async handleDeleteAdSenseKeyDb(): Promise<void> {
      try {
        await ElMessageBox.confirm(
          '确定删除数据库中的 ADSENSE_KEY 吗？删除后只能重新添加。',
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        );
        this.adsenseKeyLoading = true;
        const result = await MaintenanceApi.deleteAdSenseKeyDb();
        if (!result.success) {
          throw new Error(result.error || '删除失败');
        }
        this.adsenseKeyStatus = result.data || null;
        this.adsenseKeySource = (result.data?.source || 'file') as 'file' | 'db';
        ElMessage.success('已删除数据库 ADSENSE_KEY');
      } catch (error: any) {
        if (error === 'cancel') {
          return;
        }
        ElMessage.error('删除失败: ' + (error.message || '未知错误'));
      } finally {
        this.adsenseKeyLoading = false;
      }
    },

  },
});
</script>

<style scoped>
.maintenance-container {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 64px);
}

.maintenance-header {
  margin-bottom: 24px;
}

.maintenance-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.maintenance-header .description {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.maintenance-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.task-card {
  background-color: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.task-description {
  margin-bottom: 20px;
  color: #606266;
  line-height: 1.6;
}

.task-description p {
  margin: 0 0 8px 0;
}

.task-description code {
  background-color: #f4f4f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e6a23c;
}

.task-actions {
  margin-bottom: 20px;
}

.task-result {
  margin-top: 20px;
}

.task-result :deep(.el-alert__description) {
  white-space: pre-line;
}

.adsense-key-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.adsense-key-label {
  color: #606266;
}

.adsense-key-panel {
  margin-top: 12px;
}

.adsense-key-actions-inline {
  margin-top: 12px;
}
</style>
